import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";
import { formatHUF, getDims } from "../lib/constants";

export default function QuoteRequestDialog({ open, onOpenChange, config, price }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: "",
    gdpr_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Kérjük, töltse ki a kötelező mezőket (név, e-mail, telefon).");
      return;
    }
    if (!form.gdpr_accepted) {
      toast.error("Az ajánlatkérés elküldéséhez el kell fogadnia az Adatkezelési tájékoztatót.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, {
        config,
        customer: { ...form, location: config.location || "" },
      });
      setDone(true);
    } catch {
      toast.error("Nem sikerült elküldeni az ajánlatkérést. Kérjük próbálja újra.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setForm({ full_name: "", email: "", phone: "", message: "", gdpr_accepted: false });
    }, 400);
  };

  const dims = getDims(config);

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        data-testid="quote-dialog"
        className="bg-[#031a20] border border-[#d4af37]/30 text-white max-w-lg max-h-[90vh] overflow-y-auto"
      >
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#d4af37] mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl">Köszönjük!</h2>
            <p className="text-sm text-white/70 mt-3">
              Az ajánlatkérést sikeresen beküldte. Hamarosan visszajelzünk Önnek a megadott e-mail címen.
            </p>
            <button
              onClick={close}
              data-testid="quote-dialog-close"
              className="mt-6 bg-[#d4af37] text-[#031a20] px-8 py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#e5c865]"
            >
              Bezárás
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#d4af37]" />
                Ajánlatkérés beküldése
              </DialogTitle>
              <DialogDescription className="text-white/60 text-sm">
                {dims.width} × {dims.projection} cm pergola{config.location ? ` – ${config.location}` : ""}
                {price ? ` – Becsült bruttó ár: ${formatHUF(price)}` : ""}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <Field label="Teljes név *" v={form.full_name} on={(v) => setForm((f) => ({ ...f, full_name: v }))} testid="quote-name" />
              <Field label="E-mail cím *" type="email" v={form.email} on={(v) => setForm((f) => ({ ...f, email: v }))} testid="quote-email" />
              <Field label="Telefonszám *" type="tel" v={form.phone} on={(v) => setForm((f) => ({ ...f, phone: v }))} testid="quote-phone" />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/70 mb-2 block">Megjegyzés</label>
                <textarea
                  data-testid="quote-message"
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  rows={3}
                  className="w-full bg-[#04222a] border border-white/20 focus:border-[#d4af37] focus:outline-none px-4 py-3 text-sm text-white"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer text-[12px] text-white/80 leading-snug">
                <input
                  type="checkbox"
                  data-testid="quote-gdpr-checkbox"
                  checked={form.gdpr_accepted}
                  onChange={(e) => setForm((f) => ({ ...f, gdpr_accepted: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-[#d4af37] shrink-0"
                />
                <span>
                  Az ajánlatkérés elküldésével hozzájárulok, hogy a SolarSimple a megadott személyes adataimat
                  (név, e-mail, telefonszám, helyszín) az ajánlatadás és kapcsolatfelvétel céljából a GDPR
                  (EU 2016/679) rendelet előírásainak megfelelően kezelje és tárolja. Adataimat harmadik fél
                  részére nem adjuk át, és az ajánlatadás lezárását követő 12 hónapon belül töröljük, kivéve,
                  ha hosszabb tárolás jogszabályi kötelezettségünk. *
                </span>
              </label>

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={submitting}
                className="w-full bg-[#d4af37] text-[#031a20] hover:bg-[#e5c865] py-4 uppercase tracking-[0.2em] text-xs disabled:opacity-50 font-medium"
              >
                {submitting ? "Küldés..." : "Ajánlatkérés beküldése"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-white/70 mb-2 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-[#04222a] border border-white/20 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37] px-4 py-3 text-sm text-white"
      />
    </div>
  );
}
